import { useState, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * sessionStorage key holding the admin/dev subscription override, written by
 * SubscriptionToggleFAB and AdminFAB. Lets an admin exercise Pro and Standard behaviour without
 * changing real team data.
 */
export const SUBSCRIPTION_TYPE_OVERRIDE_KEY = 'subscription_type_override';

/** Window event dispatched when the override changes, so every reader updates at once. */
export const SUBSCRIPTION_TYPE_CHANGED_EVENT = 'subscriptionTypeChanged';

/**
 * Resolves the subscription type to use: the admin/dev override wins over the team's real type.
 * Pure and exported so the rule is testable without React.
 *
 * @returns a lowercased type, or undefined when neither source has one.
 */
export function resolveEffectiveSubscriptionType(
    override: string | null | undefined,
    teamSubscriptionType: string | null | undefined,
): string | undefined {
    const effective = override || teamSubscriptionType;
    return effective ? effective.toLowerCase() : undefined;
}

function readOverride(): string | null {
    try {
        return sessionStorage.getItem(SUBSCRIPTION_TYPE_OVERRIDE_KEY);
    } catch {
        // Safari private mode throws on sessionStorage access.
        return null;
    }
}

/**
 * The subscription type this session should behave as, lowercased.
 *
 * Centralizes the override + team-value pattern that was copy-pasted across the wizard pages,
 * Navigation, and the FABs.
 */
export function useEffectiveSubscriptionType(): string | undefined {
    const { user } = useAuthContext();
    const [override, setOverride] = useState<string | null>(readOverride);

    useEffect(() => {
        const handleChange = (event: Event) => {
            const detail = (event as CustomEvent).detail as { subscriptionType?: string } | undefined;
            setOverride(detail?.subscriptionType ?? readOverride());
        };
        window.addEventListener(SUBSCRIPTION_TYPE_CHANGED_EVENT, handleChange);
        return () => window.removeEventListener(SUBSCRIPTION_TYPE_CHANGED_EVENT, handleChange);
    }, []);

    return resolveEffectiveSubscriptionType(override, user?.team?.subscriptionType);
}

/** Convenience wrapper: true when the effective subscription type is Pro. */
export function useIsProSubscription(): boolean {
    return useEffectiveSubscriptionType() === 'pro';
}
